/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.generator

import io.kvision.form.upload.Upload
import io.kvision.form.upload.UploadInput
import io.kvision.html.Button
import io.kvision.modal.Confirm
import io.kvision.redux.createReduxStore
import io.kvision.utils.getContent
import kotlinx.browser.window
import kotlinx.coroutines.*
import pl.finn.forms.App
import pl.finn.forms.PodgladWindow
import pl.finn.forms.launchWithProgress
import pl.finn.forms.model.Attachment
import pl.finn.forms.model.DocumentTreeNode
import pl.finn.forms.model.PostCodeSug
import pl.finn.forms.service.DocumentService
import pl.finn.forms.service.FormsService

object DocumentFormModel : CoroutineScope by CoroutineScope(Dispatchers.Main + SupervisorJob()) {

  private val formsService = FormsService()
  private val documentService = DocumentService()
  private lateinit var appRoot: App

  val storeReduxForm =
    createReduxStore(
      ::formReducer,
      FormStructure(FormView.EMPTY_FORM)
    )

  fun createNewForm(token: String, app: App) {
    appRoot = app
    launch {
      val formData = documentService.createNewForm(token)
      storeReduxForm.dispatch(FormReduxAction.NewForm(formData))
    }
    app.activateFormTemplate()
    window.scrollTo(0.0, 0.0)
  }

  fun saveForm(validateResult: String?) {
    val state = storeReduxForm.getState()
    launchWithProgress {
      val url =
        documentService.saveForm(state.elements, state.attachments ?: emptyList(), validateResult, state.token!!)
      window.location.href = url
    }
  }

  fun refreshForm() {
    val state = storeReduxForm.getState()
    launchWithProgress {
      storeReduxForm.dispatch(FormReduxAction.RefreshForm(state))
      appRoot.activateFormTemplate()
    }
  }

  fun previewForm(button: Button) {
    val state = storeReduxForm.getState()
    launchWithProgress {
      val xml = documentService.previewForm(state.elements, state.attachments ?: emptyList(), state.token!!)
      PodgladWindow(state.token).pokazTresc(xml, button)
    }
  }

  fun openValidationMessageDialog() {
    val state = storeReduxForm.getState()
    launchWithProgress {
      val errors = documentService.validateForm(state.elements, state.attachments ?: emptyList(), state.token!!)
      ValidationWindow().showModalDialog(errors)
    }
  }

  fun editForm(token: String, app: App) {
    appRoot = app
    launch {
      val formData = documentService.editForm(token)
      storeReduxForm.dispatch(FormReduxAction.EditForm(formData))
    }
    app.activateFormTemplate()
    window.scrollTo(0.0, 0.0)
  }

  fun cancel() {
    launchWithProgress {
      val state = storeReduxForm.getState()
      val url = formsService.anuluj(state.token!!)
      window.location.href = url
    }
  }

  fun getToken(): String {
    return storeReduxForm.getState().token ?: ""
  }

  suspend fun getPostCodes(element: DocumentTreeNode): ArrayList<PostCodeSug> {
    val externalScope: CoroutineScope = GlobalScope
    val defaultDispatcher: CoroutineDispatcher = Dispatchers.Default
    var selectOptions: ArrayList<PostCodeSug> = ArrayList()
    val job = externalScope.launch(defaultDispatcher) {
      val el: List<PostCodeSug> = formsService.terytKodPocztowySugAction(element.value).postCodeSugList
      selectOptions = el as ArrayList<PostCodeSug>
    }
    job.join()
    return selectOptions
  }

  fun addAttachment(attachment: Upload, attachmentDesc: String?, parentName: String?) {
    val state = storeReduxForm.getState()
    launchWithProgress {
      attachment.value?.firstOrNull()?.let {
        val dataContent = attachment.getNativeFile(it)?.getContent()
        val parts = dataContent?.split(",")
        val mime = parts?.get(0)?.drop(5)?.dropLast(7)
        val content = parts?.getOrNull(1)
        storeReduxForm.dispatch(
          FormReduxAction.AddAttachment(
            Attachment(
              it.name,
              attachmentDesc,
              mime,
              content,
              parentName
            ), state.elements
          )
        )
      }
    }
  }

  fun removeAttachment(fileName: String) {
    val state = storeReduxForm.getState()
    Confirm.show(
      "Potwierdzenie",
      "Czy na pewno usunąć wybrany załącznik?",
      yesTitle = "Tak",
      noTitle = "Nie"
    ) {
      storeReduxForm.dispatch(FormReduxAction.RemoveAttachment(fileName, state.elements))
    }
  }
}
